import { useEffect, useState } from "react"
import { nestClient, requestNestMethod } from "../../nest/client"
import type { HostCPU, HostMemory, SystemMetrics } from "../../nest/generated"

export const HOST_RESOURCE_POLL_MS = 2_500
const STALE_AFTER_MS = 15_000
const UNAVAILABLE = "—"

export interface HostMetricView {
    available: boolean
    stale: boolean
    compact: string
    ariaLabel: string
    title: string
}

export interface QueueModeHostResourcesView {
    cpu: HostMetricView
    memory: HostMetricView
}

const unavailableCpu: HostMetricView = {
    available: false,
    stale: false,
    compact: UNAVAILABLE,
    ariaLabel: "Host CPU unavailable",
    title: "Host CPU is not available from this Nest node",
}

const unavailableMemory: HostMetricView = {
    available: false,
    stale: false,
    compact: UNAVAILABLE,
    ariaLabel: "Host memory unavailable",
    title: "Host memory is not available from this Nest node",
}

export function formatHostPercent(value: number): string {
    return `${Math.round(Math.min(100, Math.max(0, value)))}%`
}

export function formatMemoryAmount(bytes: number): string {
    const units = ["B", "KB", "MB", "GB", "TB"]
    let value = Math.max(0, bytes)
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024
        unit += 1
    }
    const digits = unit === 0 || value >= 10 || Number.isInteger(value) ? 0 : 1
    return `${value.toFixed(digits)} ${units[unit]}`
}

export function readQueueModeHostResources(metrics: SystemMetrics | null | undefined): QueueModeHostResourcesView {
    const host = metrics?.hostResources
    return {
        cpu: cpuView(host?.cpu),
        memory: memoryView(host?.memory),
    }
}

export function useQueueModeHostResources(active: boolean): QueueModeHostResourcesView {
    const [{ nodeId, ready }, setConnection] = useState(readNestConnection)
    const [metrics, setMetrics] = useState<SystemMetrics | null>(null)

    useEffect(() => {
        const sync = () => setConnection(readNestConnection())
        sync()
        return nestClient.onStatusChange(sync)
    }, [])

    useEffect(() => {
        if (!active || !ready) return
        let cancelled = false
        let timeout: ReturnType<typeof globalThis.setTimeout> | undefined
        setMetrics(null)

        const poll = async () => {
            try {
                const result = await requestNestMethod("system.metrics.read")
                if (!cancelled) setMetrics(result)
            } catch {
                if (!cancelled) {
                    // Keep the last successful snapshot so a disconnect does not
                    // fabricate 0%. The sampledAt timestamps remain the source of freshness.
                }
            }
            if (!cancelled) timeout = globalThis.setTimeout(() => void poll(), HOST_RESOURCE_POLL_MS)
        }

        void poll()
        return () => {
            cancelled = true
            if (timeout !== undefined) globalThis.clearTimeout(timeout)
        }
    }, [active, nodeId, ready])

    return readQueueModeHostResources(metrics)
}

function readNestConnection(): { nodeId: string | null; ready: boolean } {
    const diagnostics = nestClient.getDiagnostics()
    return { nodeId: diagnostics.nodeId, ready: diagnostics.status === "connected" }
}

function cpuView(cpu: HostCPU | undefined): HostMetricView {
    const usedPercent = finiteNumber(cpu?.usedPercent)
    const sampledAt = parseTimestamp(cpu?.sampledAt)
    const windowMs = finiteNumber(cpu?.windowMs)
    if (usedPercent === null || sampledAt === null || windowMs === null || windowMs <= 0) return unavailableCpu
    const stale = Date.now() - sampledAt.getTime() > STALE_AFTER_MS
    const compact = formatHostPercent(usedPercent)
    const windowLabel = formatDuration(windowMs)
    const ageLabel = formatSampleAge(sampledAt)
    return {
        available: true,
        stale,
        compact,
        ariaLabel: stale ? `Host CPU ${compact}, stale` : `Host CPU ${compact}`,
        title: stale ? `CPU ${usedPercent.toFixed(1)}% over ${windowLabel} as of ${ageLabel}` : `CPU ${usedPercent.toFixed(1)}% over ${windowLabel}`,
    }
}

function memoryView(memory: HostMemory | undefined): HostMetricView {
    const usedPercent = finiteNumber(memory?.usedPercent)
    const usedBytes = finiteNumber(memory?.usedBytes)
    const totalBytes = finiteNumber(memory?.totalBytes)
    const sampledAt = parseTimestamp(memory?.sampledAt)
    if (usedPercent === null || usedBytes === null || totalBytes === null || sampledAt === null || totalBytes <= 0) {
        return unavailableMemory
    }
    const stale = Date.now() - sampledAt.getTime() > STALE_AFTER_MS
    const compact = formatHostPercent(usedPercent)
    const usedLabel = formatMemoryAmount(usedBytes)
    const totalLabel = formatMemoryAmount(totalBytes)
    const ageLabel = formatSampleAge(sampledAt)
    return {
        available: true,
        stale,
        compact,
        ariaLabel: stale ? `Host memory ${compact}, ${usedLabel} of ${totalLabel}, stale` : `Host memory ${compact}, ${usedLabel} of ${totalLabel}`,
        title: stale
            ? `RAM ${usedLabel} of ${totalLabel} (${usedPercent.toFixed(1)}%) as of ${ageLabel}`
            : `RAM ${usedLabel} of ${totalLabel} (${usedPercent.toFixed(1)}%)`,
    }
}

function finiteNumber(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null
}

function parseTimestamp(value: unknown): Date | null {
    if (typeof value !== "string" || value === "") return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
}

function formatDuration(windowMs: number): string {
    if (windowMs < 1000) return `${Math.max(1, Math.round(windowMs))}ms`
    const seconds = windowMs / 1000
    if (seconds < 10) return `${seconds.toFixed(1)}s`
    return `${Math.round(seconds)}s`
}

function formatSampleAge(sampledAt: Date): string {
    const elapsedMs = Math.max(0, Date.now() - sampledAt.getTime())
    if (elapsedMs < 1000) return "just now"
    const seconds = Math.round(elapsedMs / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.round(seconds / 60)
    return `${minutes}m ago`
}
